import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import { Icon } from '@iconify/react';
import {
  Box,
  BoxProps,
  Collapse,
  List,
  ListItemButton,
  ListItemButtonProps,
  ListItemIcon,
  ListItemText,
  ListSubheader
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';

// Estilos e componentes estilizados
const ListSubheaderStyle = styled((props) => (
  <ListSubheader disableSticky disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.overline,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  paddingLeft: theme.spacing(5),
  color: theme.palette.text.primary
}));

interface ListItemStyleProps extends ListItemButtonProps {
  component?: React.ElementType<any>;
  to?: string;
}

const ListItemStyle = styled(ListItemButton)<ListItemStyleProps>(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(2.5),
  color: theme.palette.text.secondary,
  '&:before': {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: 'none',
    position: 'absolute',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main
  }
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

// Tipos
type NavItemProps = {
  onClick?(): void;
  title: string;
  path?: string;
  icon?: JSX.Element;
  role?: string;
  info?: JSX.Element;
  children?: NavItemProps[];
  roles?: string[];
};

// Definindo NavSectionProps
interface NavSectionProps extends BoxProps {
  isShow?: boolean | undefined;
  roles?: string[];
  navConfig: {
    subheader: string;
    items: NavItemProps[];
  }[];
}

// Função que verifica se o usuário tem permissão para ver o item
function hasPermission(item: NavItemProps, roles?: string[]): boolean {
  if (!item.role || roles?.includes(item.role)) {
    if (item.children) {
      return item.children.some((child) => hasPermission(child, roles));
    }
    return true;
  }
  return false;
}

// Função NavItem
function NavItem({
  item,
  isShow,
  onClick,
  roles,
  level = 0
}: {
  item: NavItemProps;
  isShow?: boolean | undefined;
  onClick?(): void;
  roles?: string[];
  level?: number;
}) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { title, path, icon, info, children } = item;
  const isActiveRoot = path ? !!matchPath({ path, end: children ? false : true }, pathname) : false;

  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    onClick && !open && onClick();
    setOpen(!open);
  };

  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    '&:before': { display: 'block' }
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium'
  };

  const paddingLeft = theme.spacing(5 + level * 2);

  if (children && hasPermission(item, roles)) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            paddingLeft,
            ...(isActiveRoot && activeRootStyle)
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>

          {isShow && (
            <>
              <ListItemText disableTypography primary={title} />
              {info && info}
              <Box
                component={Icon}
                icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                sx={{ width: 16, height: 16, ml: 1 }}
              />
            </>
          )}
        </ListItemStyle>

        {isShow && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((child) => (
                <NavItem
                  key={child.title}
                  item={child}
                  isShow={isShow}
                  onClick={onClick}
                  roles={roles}
                  level={level + 1}
                />
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  if (!children && hasPermission(item, roles)) {
    return (
      <ListItemStyle
        component={RouterLink}
        to={path}
        sx={{
          paddingLeft,
          ...(isActiveRoot && activeRootStyle)
        }}
      >
        <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
        {isShow && (
          <>
            <ListItemText disableTypography primary={title} />
            {info && info}
          </>
        )}
      </ListItemStyle>
    );
  }

  return null;
}

// Componente NavSection
export default function NavSection({ navConfig, isShow = true, roles, ...other }: NavSectionProps) {
  return (
    <Box {...other}>
      {navConfig.map((list) => {
        const { subheader, items } = list;
        return (
          <List key={subheader} disablePadding>
            {isShow && <ListSubheaderStyle>{subheader}</ListSubheaderStyle>}
            {items.map(
              (item: NavItemProps) =>
                hasPermission(item, roles) && (
                  <NavItem
                    key={item.title}
                    item={item}
                    isShow={isShow}
                    onClick={item.onClick}
                    roles={roles}
                  />
                )
            )}
          </List>
        );
      })}
    </Box>
  );
}
